/**
* @license Apache-2.0
*
* Copyright (c) 2023 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import args2multislice from '@stdlib/slice-base-args2multislice';
import Slice from '@stdlib/slice-ctor';
import ndims from '@stdlib/ndarray-base-ndims';
import slice from '@stdlib/ndarray-base-slice';
import normalizeIndex from '@stdlib/ndarray-base-normalize-index';
import nulls from '@stdlib/array-base-nulls';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns a shifted view of an input ndarray along a specified dimension.
*
* @param {ndarray} x - input array
* @param {integer} dim - index of dimension to slice
* @param {integer} start - starting index (inclusive)
* @param {boolean} strict - boolean indicating whether to enforce strict bounds checking
* @param {boolean} writable - boolean indicating whether a returned array should be writable
* @throws {TypeError} first argument must be an ndarray having one or more dimensions
* @throws {RangeError} dimension index exceeds the number of dimensions
* @throws {RangeError} slice exceeds array bounds
* @returns {ndarray} ndarray view
*
* @example
* import ndarray from '@stdlib/ndarray-ctor';
* import ndarray2array from '@stdlib/ndarray-to-array';
*
* var buffer = [ 1.0, 2.0, 3.0, 4.0, 5.0, 6.0 ];
* var shape = [ 3, 2 ];
* var strides = [ 2, 1 ];
* var offset = 0;
*
* var x = ndarray( 'generic', buffer, shape, strides, offset, 'row-major' );
* // returns <ndarray>
*
* var sh = x.shape;
* // returns [ 3, 2 ]
*
* var arr = ndarray2array( x );
* // returns [ [ 1.0, 2.0 ], [ 3.0, 4.0 ], [ 5.0, 6.0 ] ]
*
* var y = sliceDimensionFrom( x, 0, 1, false, false );
* // returns <ndarray>
*
* sh = y.shape;
* // returns [ 2, 2 ]
*
* arr = ndarray2array( y );
* // returns [ [ 3.0, 4.0 ], [ 5.0, 6.0 ] ]
*/
function sliceDimensionFrom( x, dim, start, strict, writable ) {
	var args;
	var N;
	var d;

	// Retrieve array meta data:
	N = ndims( x );

	// Check whether we were provided a zero-dimensional array...
	if ( N === 0 ) {
		throw new TypeError( format( '1lDF7', N ) );
	}
	// Normalize the dimension index:
	d = normalizeIndex( dim, N-1 );
	if ( d === -1 ) {
		throw new RangeError( format( '1lDF8', N, dim ) );
	}
	// Define a list of slice arguments:
	args = nulls( N );
	args[ d ] = new Slice( start, null );

	// Return a new array view:
	return slice( x, args2multislice( args ), strict, writable );
}


// EXPORTS //

export default sliceDimensionFrom;
